import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Stack from "@mui/material/Stack";
import DialogWrapperWithClose from "../../../General/DialogWrapperWithClose";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import { Tooltip } from "@mui/material";
import ReadOnlyProtectedForm from "../../../Inputs/ReadOnlyProtectedForm";
import TextInput from "../../../Inputs/TextInput";
import { EditIcon, DeleteIcon, SaveIcon } from "../../../../utils/icons/icons";
import ReadOnlyProtectedIconButton from "../../../Inputs/ReadOnlyProtectedIconButton";
import ReadOnlyProtectedButton from "../../../Inputs/ReadOnlyProtectedButton";

import {
  useSharedActions,
  useSharedDispatch,
  useSharedSelector,
} from "../../../../store/SharedProvider";
import {
  selectRobotApiConfigs,
  selectRobotRelayGateApiConfigs,
} from "../../../../store/robot-configurations/robot-configurations-selectors";
import { selectIsWebApp } from "../../../../store/app-configurations/app-configurations-selectors";
import ApiProtocol from "../../../../enums/robot/configuration/api/api-protocol";
import ApiEndpointNames from "../../../../enums/robot/configuration/api/api-endpoint-names";
import HttpFetchStatus from "../../../../enums/api/http-fetch-status";
import { getRobotApiConfig } from "../../../../interfaces/robot/robot-apis/robot-api-config";
import useValidatedInputEffect from "../../../../hooks/useValidatedInputEffect";
import { validateConfigName } from "../../../../validation/relay-gate-api-config-validation";
import useServerValidationResponseEffect from "../../../../hooks/useServerValidationResponseEffect";
import { selectGates } from "../../../../store/room/room-selectors";

const SelectedGateFormApiSelection: React.FC<{
  gateId: string;
  gateIdentifier: string;
}> = ({ gateId, gateIdentifier }) => {
  const { t } = useTranslation();
  const dispatch = useSharedDispatch();

  const {
    patchGateById,
    getRelayGateApiConfigs,
    putRelayGateApiConfigs,
    resetRelayGateConfigPutStatus,
  } = useSharedActions();

  const relayGateApiConfigs = useSharedSelector((state) =>
    selectRobotRelayGateApiConfigs(state),
  );
  const robotApiConfigs = useSharedSelector((state) =>
    selectRobotApiConfigs(state),
  );
  const isWebapp = useSharedSelector((state) => selectIsWebApp(state));
  const gates = useSharedSelector((state) => selectGates(state));

  const [gateConfigName, setGateConfigName] = useState<string | null>(null);
  const [selectedGateConfig, setSelectedGateConfig] = useState<string | null>(null);

  useEffect(() => {
    if (
      isWebapp &&
      getRobotApiConfig(ApiEndpointNames.GATES, robotApiConfigs)
        ?.apiProtocol === ApiProtocol.Relay &&
      relayGateApiConfigs.getStatus.status === HttpFetchStatus.Idle
    ) {
      dispatch(getRelayGateApiConfigs());
    }
  }, [relayGateApiConfigs.getStatus.status, robotApiConfigs]);

  const sortedIdentifiers = [...relayGateApiConfigs.data].sort((a, b) =>
    a.id.localeCompare(b.id),
  );

  useEffect(() => {
    if(relayGateApiConfigs.putStatus.status === HttpFetchStatus.Success){
      setSelectedGateConfig(null);
      setGateConfigName(null);
    }
  }, [relayGateApiConfigs.putStatus.status]);

  const selectedGateConfigs = gates
    .filter((g) => g.id !== gateId && g.id.trim() !== "")
    .map((g) => g.identifier);

  const { resetToggle, removeServerError, getServerValidationResultOrNull } =
    useServerValidationResponseEffect({
      patchError: relayGateApiConfigs.putStatus.error,
      resetPatchHandler: () => {
        if (gateConfigName != null && selectedGateConfig != null)
         dispatch(resetRelayGateConfigPutStatus())
        },
    });

  const initialGateConfigName = relayGateApiConfigs.data.filter(conf => conf.id === selectedGateConfig).map(conf => conf.name)[0];

  const {
    value: configName,
    validationResult: configNameValidationResult,
    valueChangeHandler: configNameChangeHandler,
  } = useValidatedInputEffect(
    initialGateConfigName,

    (newName) => {
      if (newName == null || selectedGateConfig == null) {
        return { isValid: true, error: null };
      }
      return (
        validateConfigName(
          newName,
          relayGateApiConfigs.data
            .filter((conf) => conf.id !== selectedGateConfig)
            .map((conf) => conf.name),
        )[0] ?? { isValid: true, error: null }
      );
    },
    setGateConfigName,
    getServerValidationResultOrNull("name"),
    removeServerError,
    resetToggle,
  );

  const finishNameEditingHandler = () => {
    const oldConfig = relayGateApiConfigs.data.find(
      (conf) => conf.id === selectedGateConfig,
    );
    if (oldConfig == null || gateConfigName == null) return;
    const updatedConfig = { ...oldConfig };
    updatedConfig.name = gateConfigName;

    const newConfigs = [
      ...relayGateApiConfigs.data.filter(
        (conf) => conf.id !== selectedGateConfig,
      ),
      updatedConfig,
    ];

    dispatch(putRelayGateApiConfigs(newConfigs));
  };

  const patchGateIdentifier = (identifier: string) => {
    dispatch(
      patchGateById({
        id: gateId,
        data: {
          gate: {
            identifier: identifier,
          },
        },
      }),
    );
  };

  const formSubmitHandler = (event: React.FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!configNameValidationResult.isValid) return;
    finishNameEditingHandler();
  };

  return (
    <React.Fragment>
      <DialogWrapperWithClose // maybe we should move this to the bottom or even a different component? -> I have tried for about 2 hours and failed...
        onClose={() => {
          setSelectedGateConfig(null);
          setGateConfigName(null);
        }} // saving multiple times is causing issues, so we don't allow clicking out, you HAVE TO click save
        open={gateConfigName !== null && selectedGateConfig !== null}
        fullWidth
        maxWidth="sm"
        title={t("relay-gate-config-name-edit")}
      >
        <ReadOnlyProtectedForm onSubmit={formSubmitHandler}>
          <DialogContent>
            <TextInput
              label={t("relay-gate-config-label")}
              value={configName ?? ""}
              onChange={configNameChangeHandler}
              validationResult={configNameValidationResult}
            />
          </DialogContent>
          <DialogActions>
            <ReadOnlyProtectedButton
              variant="contained"
              color="primary"
              size="small"
              startIcon={<SaveIcon />}
              onClick={finishNameEditingHandler}
              disabled={!configNameValidationResult.isValid}
            >
              {t("button-save")}
            </ReadOnlyProtectedButton>
          </DialogActions>
        </ReadOnlyProtectedForm>
      </DialogWrapperWithClose>

      <Grid item xs={12}>
        <FormControl fullWidth variant="outlined">
          <InputLabel id="relay-gate-config-label">
            {t("relay-gate-config-label")}
          </InputLabel>
          <Select
            label={t("relay-gate-config-label")}
            labelId="relay-gate-config-label"
            fullWidth
            value={gateIdentifier || ""}
            renderValue={(selected) => {
              const selectedItem = relayGateApiConfigs.data.find(
                (c) => c.id === selected,
              );
              return <span>{selectedItem?.name || ""}</span>;
            }}
            onChange={(e) => {
              if (!selectedGateConfigs.includes(e.target.value))
                  patchGateIdentifier(e.target.value);
            }}
          >
            <MenuItem
              value=""
              onClick={(e) => {
                e.stopPropagation();
                patchGateIdentifier("");
              }}
            >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ width: "100%", color: "secondary.main" }}
                >
                  <span style={{ flexGrow: 1 }}>{t("clear-selection")}</span>
                  <ReadOnlyProtectedIconButton size="small">
                    <DeleteIcon color="secondary" />
                  </ReadOnlyProtectedIconButton>
                </Stack>
            </MenuItem>

            {sortedIdentifiers.map((choice) => (
              <MenuItem key={choice.id} value={choice.id}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={{
                    width: "100%",
                    cursor: selectedGateConfigs.includes(choice.id)
                      ? "not-allowed"
                      : "pointer",
                  }}
                >
                  <Tooltip
                    title={
                      selectedGateConfigs.includes(choice.id)
                        ? t("gate-validation-error-repeated-identifier")
                        : ""
                    }
                  >
                    <span style={{ flexGrow: 1, opacity: selectedGateConfigs.includes(choice.id) ? 0.5 : 1 }}>{choice.name}</span>
                  </Tooltip>
                  <Tooltip title={t("relay-gate-config-name-edit-tooltip")}>
                    <ReadOnlyProtectedIconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        setGateConfigName(choice.name);
                        setSelectedGateConfig(choice.id);
                      }}
                      color="success"
                    >
                      <EditIcon />
                    </ReadOnlyProtectedIconButton>
                  </Tooltip>
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </React.Fragment>
  );
};

export default SelectedGateFormApiSelection;
